import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { AreaDeTexto, Campo, Seletor } from './campos';
import { Modal } from './Modal';

interface DescricaoFase {
  readonly chave: string;
  readonly curto: string;
}

/** Tudo texto: o formulário guarda o que foi digitado, a API é quem interpreta. */
const EM_BRANCO = {
  clinicaId: '',
  nome: '',
  documento: '',
  telefone: '',
  telefoneExtra: '',
  email: '',
  valorOriginal: '',
  vencimentoOriginal: '',
  descricaoDivida: '',
  pastaDocumentos: '',
  fase: 'AGUARDANDO_CONTATO',
  responsavelId: '',
  logradouro: '',
  cidade: '',
  uf: '',
  cep: '',
  observacoes: '',
};

type Formulario = typeof EM_BRANCO;

/**
 * Cadastro avulso de devedor.
 *
 * O caminho normal de entrada é a importação da planilha da clínica; este
 * formulário existe para o caso que chega solto, por telefone ou por e-mail.
 *
 * A validação séria mora no servidor — repeti-la aqui criaria duas regras para
 * manter em sincronia. A tela só barra o que impede o envio (clínica e nome) e
 * devolve, campo a campo, o que a API recusar.
 */
export function ModalNovoDevedor({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const navegar = useNavigate();
  const clienteConsulta = useQueryClient();
  const [dados, setDados] = useState<Formulario>(EM_BRANCO);
  /* Fora do `Formulario` porque não é campo do devedor: o processo é outro
     registro, criado logo depois, pela rota de processos. */
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [invalidos, setInvalidos] = useState<Record<string, string[]>>({});

  function definir(campo: keyof Formulario, valor: string) {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    // O erro sai assim que a pessoa mexe no campo. Mantê-lo até o próximo envio
    // deixa "Informe o nome" embaixo de um nome já digitado — a tela contradiz
    // o que a pessoa está vendo.
    setInvalidos((atual) => {
      if (!atual[campo]) return atual;
      const { [campo]: _corrigido, ...resto } = atual;
      return resto;
    });
  }

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const { data: equipe } = useQuery({
    queryKey: ['equipe', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/usuarios/selecao'),
  });

  const { data: fases } = useQuery({
    queryKey: ['fases'],
    queryFn: () => api.buscar<{ fases: DescricaoFase[] }>('/devedores/fases'),
    staleTime: Infinity,
  });

  function fechar() {
    setDados(EM_BRANCO);
    setNumeroProcesso('');
    setErro(null);
    setInvalidos({});
    aoFechar();
  }

  const cadastrar = useMutation({
    mutationFn: async () => {
      // Campo vazio é campo não informado. Mandar `""` faria a API tentar
      // interpretar o vazio como valor — e recusar o cadastro por isso.
      const corpo = Object.fromEntries(
        Object.entries(dados).filter(([, valor]) => valor !== ''),
      );
      const devedor = await api.criar<{ id: string; nome: string }>('/devedores', corpo);

      /* O processo é registro próprio, e só nasce se houver número. A falha aqui
         NÃO derruba o cadastro: o devedor já existe, e mandar de volta para o
         formulário faria a usuária cadastrá-lo duas vezes. O número se registra
         na ficha, onde ela já vai cair. */
      if (numeroProcesso.trim()) {
        try {
          await api.criar(`/devedores/${devedor.id}/processos`, {
            numeroProcesso: numeroProcesso.trim(),
          });
        } catch {
          /* segue para a ficha */
        }
      }

      return devedor;
    },
    onSuccess: (devedor) => {
      void clienteConsulta.invalidateQueries({ queryKey: ['devedores'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['contagens'] });
      fechar();
      // Cadastrar é só o começo: o próximo passo é sempre lançar o acordo ou as
      // parcelas, e isso é na ficha. Levar para lá evita a busca manual logo depois.
      void navegar(`/devedores/${devedor.id}`);
    },
    onError: (e) => {
      if (e instanceof FalhaDaApi) {
        setErro(e.message);
        setInvalidos(e.camposInvalidos ?? {});
        return;
      }
      setErro('Não foi possível cadastrar o devedor.');
    },
  });

  function submeter(evento: React.FormEvent) {
    evento.preventDefault();
    const faltando: Record<string, string[]> = {};
    if (!dados.clinicaId) faltando['clinicaId'] = ['Selecione a clínica credora.'];
    if (dados.nome.trim().length < 2) faltando['nome'] = ['Informe o nome do devedor.'];

    setInvalidos(faltando);
    if (Object.keys(faltando).length > 0) {
      setErro(null);
      return;
    }
    cadastrar.mutate();
  }

  /** Primeira mensagem do campo, no formato que os campos esperam. */
  const erroDe = (campo: string) => {
    const mensagem = invalidos[campo]?.[0];
    return mensagem ? { erro: mensagem } : {};
  };

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo="Novo devedor"
      descricao="Para casos avulsos — a carteira inteira de uma clínica entra pela importação."
      largura="46rem"
      rodape={
        <>
          <Botao onClick={fechar} desabilitado={cadastrar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={cadastrar.isPending}
          >
            {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar devedor'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-5">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Seletor
            rotulo="Clínica credora"
            valor={dados.clinicaId}
            aoMudar={(v) => definir('clinicaId', v)}
            vazio="Selecione…"
            opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
            {...erroDe('clinicaId')}
          />
          <Seletor
            rotulo="Fase inicial"
            valor={dados.fase}
            aoMudar={(v) => definir('fase', v)}
            opcoes={(fases?.fases ?? []).map((f) => ({ valor: f.chave, rotulo: f.curto }))}
            {...erroDe('fase')}
          />
        </div>

        <Campo
          rotulo="Nome completo"
          valor={dados.nome}
          aoMudar={(v) => definir('nome', v)}
          placeholder="Maria Aparecida de Souza"
          {...erroDe('nome')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="CPF ou CNPJ"
            valor={dados.documento}
            aoMudar={(v) => definir('documento', v)}
            placeholder="000.000.000-00"
            auxilio="Dígito errado não impede o cadastro — fica marcado como suspeito."
            {...erroDe('documento')}
          />
          <Seletor
            rotulo="Responsável"
            valor={dados.responsavelId}
            aoMudar={(v) => definir('responsavelId', v)}
            vazio="— sem responsável —"
            opcoes={(equipe ?? []).map((u) => ({ valor: u.id, rotulo: u.nome }))}
            {...erroDe('responsavelId')}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Telefone"
            valor={dados.telefone}
            aoMudar={(v) => definir('telefone', v)}
            placeholder="(14) 98142-7809"
            auxilio="Sem telefone, a régua de cobrança não alcança o caso."
            {...erroDe('telefone')}
          />
          <Campo
            rotulo="Telefone alternativo"
            valor={dados.telefoneExtra}
            aoMudar={(v) => definir('telefoneExtra', v)}
            placeholder="opcional"
            {...erroDe('telefoneExtra')}
          />
        </div>

        <Campo
          rotulo="E-mail"
          tipo="email"
          valor={dados.email}
          aoMudar={(v) => definir('email', v)}
          placeholder="opcional"
          {...erroDe('email')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Valor da dívida na origem"
            valor={dados.valorOriginal}
            aoMudar={(v) => definir('valorOriginal', v)}
            placeholder="1.850,00"
            auxilio="Valor de origem, não o saldo atual."
            {...erroDe('valorOriginal')}
          />
          <Campo
            rotulo="Vencimento original"
            tipo="date"
            valor={dados.vencimentoOriginal}
            aoMudar={(v) => definir('vencimentoOriginal', v)}
            {...erroDe('vencimentoOriginal')}
          />
        </div>

        <Campo
          rotulo="Descrição da dívida"
          valor={dados.descricaoDivida}
          aoMudar={(v) => definir('descricaoDivida', v)}
          placeholder="Tratamento ortodôntico — 6 parcelas em aberto"
          {...erroDe('descricaoDivida')}
        />

        <fieldset className="space-y-4 border-t pt-5">
          <legend className="sr-only">Endereço</legend>
          <p className="text-xs text-[color:var(--texto-tenue)]">
            Endereço — dispensável agora, indispensável para citar o devedor se o caso
            for ao judicial.
          </p>

          <Campo
            rotulo="Logradouro"
            valor={dados.logradouro}
            aoMudar={(v) => definir('logradouro', v)}
            placeholder="Rua, número, complemento"
            {...erroDe('logradouro')}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_6rem_9rem]">
            <Campo
              rotulo="Cidade"
              valor={dados.cidade}
              aoMudar={(v) => definir('cidade', v)}
              {...erroDe('cidade')}
            />
            <Campo
              rotulo="UF"
              valor={dados.uf}
              aoMudar={(v) => definir('uf', v.toUpperCase())}
              placeholder="SP"
              {...erroDe('uf')}
            />
            <Campo
              rotulo="CEP"
              valor={dados.cep}
              aoMudar={(v) => definir('cep', v)}
              placeholder="14780-000"
              {...erroDe('cep')}
            />
          </div>
        </fieldset>

        {/* Devedor que já chega com ação distribuída. O processo é criado logo
            depois do cadastro, pela rota própria — e é ela que move o caso para
            a fase judicial, por isso o aviso no campo. */}
        <Campo
          rotulo="Número do processo (opcional)"
          valor={numeroProcesso}
          aoMudar={setNumeroProcesso}
          placeholder="0000000-00.0000.0.00.0000"
          auxilio="Só se a ação já foi distribuída. Preenchido, o caso entra na fase judicial."
        />

        <Campo
          rotulo="Pasta no Drive (opcional)"
          valor={dados.pastaDocumentos}
          aoMudar={(v) => definir('pastaDocumentos', v)}
          placeholder="https://drive.google.com/drive/folders/…"
          auxilio="Contrato e documentos ficam lá; aqui guardamos só o caminho."
          {...erroDe('pastaDocumentos')}
        />

        <AreaDeTexto
          rotulo="Observações"
          valor={dados.observacoes}
          aoMudar={(v) => definir('observacoes', v)}
          linhas={3}
          placeholder="Contexto que ajude quem for cobrar."
        />
      </form>
    </Modal>
  );
}
